/**
 * ThreadMessage — renders a single email message in the thread (expanded or collapsed).
 * Props:
 *   emailItem  {object}  — email item from thread.items[n].email
 *   isExpanded {bool}
 *   store
 *   i18n
 */

import { h } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import { PersonAvatar } from './PersonAvatar.jsx'
import { fileIconClass, humanFileSize, formatDate, formatDateLong, emailAddressFormat } from '../../modules/email/email-helpers.js'

export function ThreadMessage({ emailItem, isExpanded, store, i18n }) {
  const bodyRef = useRef(null)

  function toggleExpand(e) {
    e.stopPropagation()
    store.toggleMessageExpanded(emailItem.id)
  }

  function startReply(type = 'reply') {
    store.openCompose(type, emailItem.id)
  }

  const toNames = (emailItem.to_addresses || []).slice(0, 2).map(a => a.name || a.address)
  const toOverflow = Math.max(0, (emailItem.to_addresses || []).length - 2)
  const hasCc = (emailItem.cc_addresses || []).length > 0

  if (!isExpanded) {
    return (
      <CollapsedMessage
        emailItem={emailItem}
        onExpand={toggleExpand}
        i18n={i18n}
      />
    )
  }

  return (
    <div
      id={`email_${emailItem.id}_thread_msg`}
      class="email-thread-message email-thread-message-expanded"
    >
      {/* ── Message header ── */}
      <div class="email-msg-header px-2 px-md-4 pt-3 pb-2">
        <div class="d-flex align-items-start gap-3">

          {/* Sender avatar — click to collapse */}
          <div
            class="flex-shrink-0 pt-1"
            style="cursor:pointer"
            title={i18n?.t('emails.collapse')}
            onClick={toggleExpand}
          >
            <PersonAvatar
              avatarUrl={emailItem.sender_avatar_url}
              name={emailItem.sender_person_name || emailItem.from_name}
              size={40}
            />
          </div>

          <div class="flex-grow-1 min-w-0">
            <div class="d-flex align-items-start justify-content-between gap-2">

              {/* Left: name + to line + detail toggle */}
              <div class="min-w-0">
                <span class="fw-semibold d-block text-truncate">{emailItem.from_name}</span>
                <div class="d-flex align-items-baseline gap-1 flex-wrap">
                  <span class="text-muted small">{i18n?.t('emails.to')}:</span>
                  <span class="small text-truncate">
                    {toNames.join(', ')}
                    {toOverflow > 0 && <span class="text-muted">+{toOverflow}</span>}
                    {hasCc && <span class="text-muted ms-1">+ CC</span>}
                  </span>
                  <DetailsToggle emailId={emailItem.id} i18n={i18n} />
                </div>

                {/* Expanded recipient details */}
                <RecipientDetails emailItem={emailItem} i18n={i18n} />
              </div>

              {/* Right: date + icons + reply shortcut */}
              <div class="d-flex align-items-center gap-2 flex-shrink-0 text-muted small">
                {emailItem.direction === 'outbound' && (
                  <span class="badge bg-primary-subtle text-primary">
                    <i class="bi bi-arrow-up-short" />
                    {i18n?.t('emails.sent')}
                  </span>
                )}
                {emailItem.files_attached && (
                  <i class="bi bi-paperclip" title={i18n?.t('emails.has_attachment')} />
                )}
                <time
                  datetime={emailItem.sent_at}
                  title={formatDateLong(emailItem.sent_at)}
                >
                  {formatDate(emailItem.sent_at)}
                </time>
                <button
                  class="btn btn-link btn-sm p-0 text-muted"
                  title={i18n?.t('emails.reply')}
                  onClick={() => startReply('reply')}
                >
                  <i class="bi bi-reply" style="font-size:0.95rem" />
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Message body ── */}
      <div class="email-msg-body px-2 px-md-4 pb-3">
        {emailItem.body_html
          ? (
            <div
              ref={bodyRef}
              class="email-html-body"
              dangerouslySetInnerHTML={{ __html: emailItem.body_html }}
            />
          )
          : (
            <div class="email-plain-body">
              <pre class="mb-0">{emailItem.body_text}</pre>
            </div>
          )
        }
      </div>

      {/* ── Attachments ── */}
      {emailItem.files_attached && (emailItem.attachments || []).length > 0 && (
        <AttachmentList attachments={emailItem.attachments} emailId={emailItem.id} i18n={i18n} />
      )}

      {/* ── Read-by avatars ── */}
      {(emailItem.read_by || []).length > 0 && (
        <div
          id={`email_${emailItem.id}_readers`}
          class="email-msg-readers px-2 px-md-4 pb-2 d-flex align-items-center gap-1 flex-wrap"
        >
          <span class="text-muted small me-1">{i18n?.t('emails.read_by')}:</span>
          {emailItem.read_by.map((r, i) => (
            <div key={i} class="read-avatar" style={{ '--avatar-z-index': 10 - i }} title={r.person_name}>
              <PersonAvatar avatarUrl={r.person_avatar_url} name={r.person_name} size={22} cssClass="border border-white" />
            </div>
          ))}
        </div>
      )}

      {/* ── Action bar ── */}
      <div class="email-msg-actions px-2 px-md-4 pb-3 pt-1 d-flex align-items-center gap-2 border-top">
        <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => startReply('reply')}>
          <i class="bi bi-reply" />
          {i18n?.t('emails.reply')}
        </button>
        <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => startReply('reply_all')}>
          <i class="bi bi-reply-all" />
          {i18n?.t('emails.reply_all')}
        </button>
        <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => startReply('forward')}>
          <i class="bi bi-forward" />
          {i18n?.t('emails.forward')}
        </button>
      </div>

    </div>
  )
}

// ── Collapsed variant ─────────────────────────────────────────────────────────

function CollapsedMessage({ emailItem, onExpand, i18n }) {
  return (
    <div
      id={`email_${emailItem.id}_thread_msg`}
      class="email-thread-message email-thread-message-collapsed"
      onClick={onExpand}
      style="cursor:pointer"
    >
      <div class="d-flex align-items-center gap-3 px-4 py-2 email-collapsed-inner">
        <div class="flex-shrink-0">
          <PersonAvatar
            avatarUrl={emailItem.sender_avatar_url}
            name={emailItem.sender_person_name || emailItem.from_name}
            size={32}
          />
        </div>
        <div class="flex-grow-1 min-w-0 overflow-hidden">
          <div class="d-flex align-items-baseline gap-2">
            <span class="fw-medium small flex-shrink-0">{emailItem.from_name}</span>
            <span class="text-muted small text-truncate" style="font-size:0.78rem">
              {emailItem.snippet || (emailItem.body_text || '').slice(0, 100)}
            </span>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0 text-muted">
          {emailItem.files_attached && <i class="bi bi-paperclip small" />}
          <span class="text-nowrap" style="font-size:0.75rem">
            {formatDate(emailItem.sent_at)}
          </span>
          <i class="bi bi-chevron-down small" style="font-size:0.65rem" />
        </div>
      </div>
    </div>
  )
}

// ── Details toggle ────────────────────────────────────────────────────────────

function DetailsToggle({ emailId, i18n }) {
  function toggle(e) {
    e.preventDefault()
    const el = document.getElementById(`recipient_details_${emailId}`)
    if (el) el.classList.toggle('d-none')
  }
  return (
    <button
      class="btn btn-link btn-sm p-0 text-muted"
      style="font-size:0.72rem;line-height:1"
      onClick={toggle}
    >
      {i18n?.t('emails.details')}
      <i class="bi bi-chevron-down" style="font-size:0.6rem" />
    </button>
  )
}

// ── Recipient details ─────────────────────────────────────────────────────────

function RecipientDetails({ emailItem, i18n }) {
  return (
    <div class="email-recipient-details d-none mt-1" id={`recipient_details_${emailItem.id}`}>
      <table class="small" style="border-spacing:4px 2px;border-collapse:separate">
        <tr>
          <td class="text-muted pe-2 align-top fw-medium">{i18n?.t('emails.from_label') || 'Feladó'}:</td>
          <td>{emailAddressFormat(emailItem.from_address ? { address: emailItem.from_address, name: emailItem.from_name } : null)}</td>
        </tr>
        {(emailItem.to_addresses || []).length > 0 && (
          <tr>
            <td class="text-muted pe-2 align-top fw-medium">{i18n?.t('emails.to_label')}:</td>
            <td>
              {emailItem.to_addresses.map((a, i) => (
                <div key={i}>{emailAddressFormat(a)}</div>
              ))}
            </td>
          </tr>
        )}
        {(emailItem.cc_addresses || []).length > 0 && (
          <tr>
            <td class="text-muted pe-2 align-top fw-medium">{i18n?.t('emails.cc_label')}:</td>
            <td>
              {emailItem.cc_addresses.map((a, i) => (
                <div key={i}>{emailAddressFormat(a)}</div>
              ))}
            </td>
          </tr>
        )}
        {emailItem.mailbox_name && (
          <tr>
            <td class="text-muted pe-2 fw-medium">{i18n?.t('emails.mailbox_label')}:</td>
            <td>{emailItem.mailbox_name} &lt;{emailItem.mailbox_email}&gt;</td>
          </tr>
        )}
      </table>
    </div>
  )
}

// ── Attachment list ───────────────────────────────────────────────────────────

function AttachmentList({ attachments, emailId, i18n }) {
  return (
    <div class="email-msg-attachments px-2 px-md-4 pb-3">
      <div class="d-flex align-items-center gap-1 mb-2">
        <i class="bi bi-paperclip text-muted small" />
        <span class="small text-muted fw-medium">
          {i18n?.t('emails.attachments_count', { count: attachments.length })}
        </span>
      </div>
      <div class="d-flex flex-wrap gap-2">
        {attachments.map(file => (
          <div key={file.id} class="email-attachment-chip-wrap d-inline-flex align-items-stretch">
            <a
              href={file.download_url}
              class="email-attachment-chip text-decoration-none"
              download={file.filename}
              data-turbo="false"
            >
              <i class={`bi ${fileIconClass(file.filename)} text-muted`} />
              <span class="small text-truncate" style="max-width:140px">{file.filename}</span>
              <span class="text-muted" style="font-size:0.7rem">{humanFileSize(file.byte_size)}</span>
            </a>
            <a
              href={file.download_url}
              class="email-attachment-download"
              title={i18n?.t('helpers.download')}
              download={file.filename}
              data-turbo="false"
            >
              <i class="bi bi-download" />
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}
